/**
 * Generic agent-streamed data grid.
 *
 * Used by the Results tab on the artifact drawer and reusable for any future
 * surface where the agent streams tabular output (cohort prevalence, analytical variable
 * sample rows, KPI snapshots). The agent emits a START with `columns` and
 * optional `summaryStats`, then appends `rows` until END — this component
 * just renders whatever is in state. No row count limits, no virtualization
 * yet (the slugged result previews are by design small — the live feel of
 * rows landing one at a time is what matters; if datasets grow large the
 * agent should stream a sampled preview, not the whole table).
 */

import { Box, Chip, Stack, Typography } from '@mui/material';

import type { EpiArtifactResult } from '../types/epi-chat-types';

interface AgenticDataGridProps {
  result: EpiArtifactResult;
  /** Caps the number of rows rendered (the rest are summarized). */
  maxRows?: number;
  testIdPrefix?: string;
}

const formatCell = (value: string | number | null): string => {
  if (value === null) return '—';
  if (typeof value === 'number') return value.toLocaleString();
  return value;
};

export const AgenticDataGrid = ({
  result,
  maxRows = 200,
  testIdPrefix = 'agentic-data-grid',
}: AgenticDataGridProps) => {
  const renderRows = result.rows.slice(0, maxRows);
  const truncated = result.rows.length > maxRows;
  const totalKnown = result.rowCount ?? result.rows.length;

  return (
    <Stack spacing={1.5} data-testid={testIdPrefix}>
      {result.summaryStats && result.summaryStats.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          sx={{ gap: 1 }}
          data-testid={`${testIdPrefix}-stats`}
        >
          {result.summaryStats.map((stat) => (
            <Box
              key={stat.label}
              sx={{
                paddingX: 1.5,
                paddingY: 1,
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: (theme) => theme.palette.background.paper,
                minWidth: 100,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em' }}
              >
                {stat.label}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {stat.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="caption" color="text.secondary">
          {result.status === 'streaming'
            ? `streaming… ${result.rows.length.toLocaleString()} rows`
            : truncated
            ? `showing ${renderRows.length.toLocaleString()} of ${totalKnown.toLocaleString()} rows`
            : `${totalKnown.toLocaleString()} rows`}
        </Typography>
        {result.status === 'streaming' && (
          <Chip
            size="small"
            label="live"
            color="primary"
            variant="outlined"
            sx={{ height: 18, fontSize: (theme) => theme.typography.caption.fontSize }}
          />
        )}
      </Stack>

      {result.columns.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          (no columns yet — waiting for the agent to define the result shape)
        </Typography>
      ) : (
        <Box
          sx={{
            overflow: 'auto',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
          }}
        >
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: (theme) => theme.typography.body2.fontSize,
              fontFamily: 'monospace',
            }}
            data-testid={`${testIdPrefix}-table`}
          >
            <Box component="thead">
              <Box component="tr">
                {result.columns.map((col) => (
                  <Box
                    component="th"
                    key={col}
                    sx={{
                      textAlign: 'left',
                      paddingX: 1,
                      paddingY: 0.75,
                      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                      backgroundColor: (theme) => theme.palette.action.hover,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {renderRows.length === 0 ? (
                <Box component="tr">
                  <Box
                    component="td"
                    colSpan={result.columns.length}
                    sx={{
                      paddingX: 1,
                      paddingY: 1.5,
                      color: 'text.secondary',
                      fontStyle: 'italic',
                    }}
                  >
                    {result.status === 'streaming' ? 'waiting for first row…' : 'no rows returned'}
                  </Box>
                </Box>
              ) : (
                renderRows.map((row, rowIdx) => (
                  <Box
                    component="tr"
                    key={rowIdx}
                    sx={{
                      '&:nth-of-type(even)': {
                        backgroundColor: (theme) => theme.palette.action.hover,
                      },
                    }}
                  >
                    {result.columns.map((_, colIdx) => (
                      <Box
                        component="td"
                        key={colIdx}
                        sx={{
                          paddingX: 1,
                          paddingY: 0.5,
                          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                          whiteSpace: 'nowrap',
                          color: row[colIdx] === null ? 'text.disabled' : 'text.primary',
                        }}
                      >
                        {formatCell(row[colIdx] ?? null)}
                      </Box>
                    ))}
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Stack>
  );
};
